/**
 * The structures of a system as the session streams them (M3 plan R12, §2.6.3): every station, relay, lane ring and
 * POI of the layout, with its render look, seed and asset. The persistent state (relay online, ring up, POI looted)
 * is read from the Reach state when a structure streams in, so a structure that streams back shows the latest state.
 * Built once per system entry (a rare event); allocates freely.
 */
import type { FactionsConfig } from '../../galaxy/layout/config'
import type { SystemLayout } from '../../galaxy/layout/types'
import type { Galaxy } from '../../galaxy/types'
import { deltaOf } from '../state/deltas'
import { laneRingUp, relayStateOf } from '../state/relays'
import type { ActiveStructure } from '../runtime/types'
import type { ReachState } from '../types'
import type { SessionConfig } from './config'

/** A structure the session can stream: an `ActiveStructure` minus its live state. */
export type StructureSite = Omit<ActiveStructure, 'state'>

/** Relay state codes of `ActiveStructure.state`. */
export const RELAY_STATE = { offline: 0, online: 1, reboot: 2 } as const

/**
 * The layout's structures in a stable order: stations, relays, lane rings, then POIs, each by index. Relays carry
 * their galaxy relay index as the seed; lane rings their ring index; stations their price seed; POIs their own seed.
 */
export function structureSites(layout: SystemLayout, factions: FactionsConfig, cfg: SessionConfig): StructureSite[] {
  const out: StructureSite[] = []
  for (const s of layout.stations) {
    out.push({ kind: 'station', index: s.index, id: s.id, assetId: s.assetId, position: s.position, rotation: s.rotation, seed: s.priceSeed, look: s.look })
  }
  for (const r of layout.relays) {
    out.push({ kind: 'relay', index: r.index, id: r.relayId, assetId: r.assetId, position: r.position, rotation: r.rotation, seed: r.index, look: cfg.looks.relay })
  }
  for (const r of layout.laneRings) {
    const lane = layout.lanes[r.laneIndex]
    const look = lane === undefined ? cfg.looks.relay : factions.factions[lane.faction].look
    out.push({ kind: 'laneRing', index: r.index, id: r.id, assetId: r.assetId, position: r.position, rotation: r.rotation, seed: r.index, look })
  }
  for (const p of layout.pois) {
    const look = p.kind === 'hidden-base' && p.baseSide === 'guardian' ? cfg.looks.guardianBase : cfg.looks.poi[p.kind]
    out.push({ kind: 'poi', index: p.index, id: p.id, assetId: p.assetId, position: p.position, rotation: p.rotation, seed: p.seed, look })
  }
  return out
}

/** Every distinct model the layout's structures use (derelicts are assembled from the boot-loaded station kit). */
export function structureAssetIds(layout: SystemLayout): string[] {
  const ids = new Set<string>()
  for (const s of layout.stations) ids.add(s.assetId)
  for (const r of layout.relays) ids.add(r.assetId)
  for (const r of layout.laneRings) ids.add(r.assetId)
  for (const p of layout.pois) if (p.assetId !== null) ids.add(p.assetId)
  return [...ids].sort()
}

/**
 * The live state of a structure from the Reach state: relay 0 offline, 1 online, 2 reboot; lane ring 0 down, 1 up;
 * POI 0 intact, 1 looted or cleared; stations 0.
 */
export function structureStateOf(site: StructureSite, layout: SystemLayout, state: ReachState, galaxy: Galaxy): number {
  switch (site.kind) {
    case 'station':
      return 0
    case 'relay':
      return RELAY_STATE[relayStateOf(state, galaxy, site.id).status]
    case 'laneRing': {
      const ring = layout.laneRings[site.index]
      return ring !== undefined && laneRingUp(state, ring) ? 1 : 0
    }
    case 'poi': {
      const d = deltaOf(state, layout.systemId)
      return d.lootedPois.includes(site.id) || d.clearedBases.includes(site.id) ? 1 : 0
    }
  }
}
